/**
 * 画像の全画面表示。
 *
 * 詳細画面の画像をクリックすると、画面いっぱいに拡大する。
 * さらにクリック（またはホイール／ピンチ）で等倍まで拡大でき、
 * ドラッグで見たい場所へ動かせる。
 *
 * 【キーボードとフォーカス】
 * 開いている間は背後をスクロールさせない。Esc で閉じる。
 * モーダルなので、開いた時点で閉じるボタンへフォーカスを移す
 * （キーボードだけで閉じられないと詰む）。
 */
import { useEffect, useRef, useState } from 'react'

export function Lightbox({
  src,
  alt,
  onClose,
}: {
  src: string
  alt: string
  onClose: () => void
}) {
  const [zoomed, setZoomed] = useState(false)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const dragging = useRef<{ x: number; y: number } | null>(null)
  const closeRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    closeRef.current?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)

    // 背後がスクロールすると、閉じたときに位置がずれる
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [onClose])

  const toggleZoom = () => {
    setZoomed((z) => !z)
    setOffset({ x: 0, y: 0 })
  }

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={onClose}
    >
      <button ref={closeRef} className="lightbox-close" onClick={onClose} aria-label="閉じる">
        ×
      </button>

      <img
        src={src}
        alt={alt}
        className={zoomed ? 'zoomed' : undefined}
        draggable={false}
        style={
          zoomed ? { transform: `translate(${offset.x}px, ${offset.y}px)` } : undefined
        }
        // 画像自身のクリックは閉じずに拡大切り替えへ回す
        onClick={(e) => {
          e.stopPropagation()
          if (!dragging.current) toggleZoom()
        }}
        onPointerDown={(e) => {
          if (!zoomed) return
          dragging.current = { x: e.clientX - offset.x, y: e.clientY - offset.y }
          ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
        }}
        onPointerMove={(e) => {
          if (!dragging.current) return
          setOffset({ x: e.clientX - dragging.current.x, y: e.clientY - dragging.current.y })
        }}
        onPointerUp={(e) => {
          const moved = dragging.current
          dragging.current = null
          ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
          // ドラッグ直後のクリックで拡大が切り替わらないようにする
          if (moved) e.stopPropagation()
        }}
      />

      <p className="lightbox-hint">
        {zoomed ? 'クリックで縮小・ドラッグで移動' : 'クリックで拡大／Esc で閉じる'}
      </p>
    </div>
  )
}
